import { useTranslation } from "react-i18next";
import { Shield, UserCircle2, Crown } from "lucide-react";
import styles from "./PortalUserStrip.module.css";
import DefaultUserImage from "../../../assets/images/default-user.webp";

const iconProps = { size: 18, strokeWidth: 2, "aria-hidden": true };

export default function PortalUserStrip({ user }) {
  const { t, i18n } = useTranslation("dashboard");

  const fullName = user?.name?.trim() || t("userCard.notProvided");
  const email = user?.email?.trim() || t("userCard.notProvided");
  const phoneRaw = user?.phoneNumber?.trim();
  const hasPhone = !!(phoneRaw && phoneRaw.length > 0);
  const phoneDisplay = hasPhone ? phoneRaw : t("userCard.notProvided");

  const companyRaw = user?.company?.trim();
  const hasCompany = !!(companyRaw && companyRaw.length > 0);
  const companyDisplay = hasCompany ? companyRaw : t("userCard.notSpecified");

  const imgSrc =
    user?.profileImage &&
    user.profileImage !== "null" &&
    user.profileImage != null
      ? user.profileImage
      : DefaultUserImage;

  const role = (user?.role || "user").toLowerCase();
  const isActive = user?.isActive !== false;

  const roleLabel = () => {
    if (role === "owner") return t("portalUser.roleOwner");
    if (role === "admin") return t("portalUser.roleAdmin");
    return t("portalUser.roleUser");
  };

  const RoleIcon =
    role === "owner" ? Crown : role === "admin" ? Shield : UserCircle2;

  const locale = i18n.language === "ro" ? "ro-RO" : "en-US";

  const formatDateOnly = (ts) => {
    if (ts == null) return null;
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(locale, { dateStyle: "long" });
  };

  const memberSince = formatDateOnly(user?.createdAt);
  const memberSinceDisplay =
    memberSince ?? t("portalUser.memberSinceUnknown");

  return (
    <div className={styles.strip}>
      <div className={styles.main}>
        <div className={styles.avatarColumn}>
          <div className={styles.avatarWrap}>
            <img
              src={imgSrc}
              alt=""
              className={styles.avatar}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = DefaultUserImage;
              }}
            />
            <span
              className={`${styles.avatarStatus} ${
                isActive ? styles.avatarStatusOn : styles.avatarStatusOff
              }`}
              title={
                isActive
                  ? t("portalUser.accountActive")
                  : t("portalUser.accountInactive")
              }
            >
              {isActive
                ? t("portalUser.accountActive")
                : t("portalUser.accountInactive")}
            </span>
          </div>
        </div>
        <div className={styles.mainBody}>
          <div className={styles.identity}>
            <div className={styles.nameRow}>
              <h2 className={styles.name}>{fullName}</h2>
              <span
                className={`${styles.rolePill} ${
                  styles[`role_${role}`] || styles.role_user
                }`}
              >
                <RoleIcon {...iconProps} className={styles.roleIcon} />
                {roleLabel()}
              </span>
            </div>
            <p className={styles.email}>{email}</p>
          </div>

          <div className={styles.detailList}>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>
                {t("userCard.phoneNumber")}
              </span>
              <span
                className={
                  hasPhone ? styles.detailValue : styles.detailValueAccent
                }
              >
                {phoneDisplay}
              </span>
            </div>
            <hr className={styles.detailDivider} />
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>{t("userCard.company")}</span>
              <span
                className={
                  hasCompany ? styles.detailValue : styles.detailValueMuted
                }
              >
                {companyDisplay}
              </span>
            </div>
            <hr className={styles.detailDivider} />
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>
                {t("userCard.memberSince")}
              </span>
              <span className={styles.detailValue}>{memberSinceDisplay}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
